'use client';

import { Dialog } from '@base-ui/react/dialog';
import { RoundupDriverGroupEnum } from '@repo/contracts/marketplace/offers/enums';
import type { Offer } from '@repo/contracts/marketplace/offers/types';
import { css } from '@repo/pandacss/css';
import { CloseIcon } from '@repo/ui/icons/CloseIcon';
import { getOrCreateId } from '@repo/utils/static-ids';
import { useRef } from 'react';
import { BRANCH_DRIVER_GROUP_MAP } from '@/features/funnel/config/drivers';
import { useBranchId, useFunnelStepId } from '@/features/funnel/state/funnel/Context/hooks';
import { ProductCardEntrypoint } from '@/features/product-card';
import { usePageActionForBranch } from '@/features/shared/analytics/hooks/usePageActionForBranch';
type Props = {
  offer: Offer | null;
  onClose: () => void;
};
export function DetailsModal({
  offer,
  onClose
}: Props) {
  const activityIdRef = useRef('');
  const activityId = getOrCreateId(activityIdRef);
  const stepId = useFunnelStepId();
  const branchId = useBranchId();
  const firePageAction = usePageActionForBranch();
  const firePageActionForModalClose = () => {
    firePageAction({
      action_name: 'partner_modal_exit',
      action_type: 'close',
      activity_id: activityId
    });
  };
  const driverGroup = branchId ? BRANCH_DRIVER_GROUP_MAP[branchId] : RoundupDriverGroupEnum.Default;
  return <Dialog.Root open={offer !== null} onOpenChange={open => {
    if (!open) {
      firePageActionForModalClose();
      onClose();
    }
  }}>
      <Dialog.Portal>
        <Dialog.Backdrop className={css({
        position: 'fixed',
        inset: '[0]',
        bg: '[rgba(0,0,0,0.5)]',
        zIndex: '[1000]'
      })} />
        <Dialog.Viewport className={css({
        position: 'fixed',
        inset: '[0]',
        zIndex: '[1001]',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: '4'
      })}>
          <Dialog.Popup className={css({
          bg: 'common.white',
          width: '[100%]',
          maxWidth: '[1152px]',
          borderRadius: '[24px]',
          height: '[min(90vh,646px)]',
          maxHeight: '[90vh]',
          minHeight: '[0]',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        })}>
            <div className={css({
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            height: '[48px]',
            pt: '3',
            px: '3',
            flexShrink: '0'
          })}>
              <Dialog.Close render={<button type="button" aria-label="Close card details" className={css({
              bg: '[transparent]',
              border: 'none',
              cursor: 'pointer',
              color: 'neutral.darker',
              width: '[32px]',
              height: '[32px]',
              rounded: 'full',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: '[0]',
              _hover: {
                bg: 'neutral.lightest'
              }
            })}>
                    <CloseIcon fill="currentColor" />
                  </button>} />
            </div>
            <div className={css({
            overflowY: 'auto',
            flex: '[1 1 0]',
            minHeight: '[0]'
          })}>
              {offer && <ProductCardEntrypoint analytics={{
              sectionName: 'offer_modal',
              sectionIndex: 0,
              elementPosition: 0,
              topic: 'credit-cards',
              additionalAttributes: {
                page_branch_name: branchId,
                page_name: stepId
              }
            }} creditCardOffer={offer} driverGroup={driverGroup} hideOuterBorder />}
            </div>
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>;
}